import { Button, CircularProgress, makeStyles, Paper, Typography } from "@material-ui/core";
import { Alert, Skeleton } from "@material-ui/lab";
import React, { useRef, useState } from "react";
import { MdDeleteOutline, MdHistory, MdRestore } from "react-icons/md";
import { Prompt, useHistory } from "react-router-dom";
import { formatDateTime, SysadminSection, sysadminPaths } from "../sysadminUtils";
import { ConfirmDialog } from "./ConfirmDialog";
import { Crumb, PageHeader } from "./PageHeader";
import { EntityMeta } from "./useEntityEditor";

const useStyles = makeStyles(theme => ({
    section: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    denseSection: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    sectionTitle: {
        fontWeight: 700,
        marginBottom: theme.spacing(2),
    },
    sectionDescription: {
        marginTop: theme.spacing(-1.5),
        marginBottom: theme.spacing(2),
    },
    actionBar: {
        position: "sticky",
        bottom: 0,
        zIndex: 2,
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: theme.spacing(2),
        padding: theme.spacing(1.5, 3),
        marginBottom: theme.spacing(3),
    },
    actionInfo: {
        marginRight: "auto",
    },
    deleteButton: {
        color: theme.palette.error.main,
    },
    banner: {
        marginBottom: theme.spacing(3),
    },
}));

export const Section: React.FC<{ title: string; description?: string; dense?: boolean }> = ({ title, description, dense, children }) => {
    const classes = useStyles();
    return (
        <Paper variant="outlined" className={dense ? classes.denseSection : classes.section}>
            <Typography variant={dense ? "subtitle1" : "h6"} component="h2" className={classes.sectionTitle}>{title}</Typography>
            {description && <Typography variant="body2" color="textSecondary" className={classes.sectionDescription}>{description}</Typography>}
            {children}
        </Paper>
    );
};

interface SaveBarProps {
    /** Human readable list of what is missing before saving is possible. */
    missing: string[];
    info: string;
    saving: boolean;
    canSave: boolean;
    saveLabel: string;
    onCancel: () => void;
    onSave: () => void;
}

/** Sticky bar at the bottom of a detail page with Cancel and Save. */
export const SaveBar: React.FC<SaveBarProps> = ({ missing, info, saving, canSave, saveLabel, onCancel, onSave }) => {
    const classes = useStyles();
    return (
        <Paper elevation={3} className={classes.actionBar}>
            <Typography variant="body2" color={missing.length > 0 ? "error" : "textSecondary"} className={classes.actionInfo}>
                {missing.length > 0 ? "Missing: " + missing.join(", ") : info}
            </Typography>
            <Button onClick={onCancel} disabled={saving}>
                Cancel
            </Button>
            <Button
                variant="contained"
                color="primary"
                onClick={onSave}
                disabled={saving || missing.length > 0 || !canSave}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
                {saveLabel}
            </Button>
        </Paper>
    );
};

interface EditorState {
    entity: { id: string };
    isNew: boolean;
    isVersion: boolean;
    loading: boolean;
    loadError?: string;
    reloadEntity: () => void;
    meta?: EntityMeta;
    dirty: boolean;
    saving: boolean;
    save: () => Promise<boolean>;
    remove: (redirectTo: string) => Promise<boolean>;
    reset: () => void;
}

interface Props {
    label: string;
    section: SysadminSection;
    title: string;
    breadcrumbs: Crumb[];
    editor: EditorState;
    /** Human readable list of what is missing before the entity can be saved. */
    missing: string[];
    /** Where Cancel and Delete lead to (typically the parent entity). */
    parentPath: string;
    deleteWarning?: React.ReactNode;
    /** Content shown below the form for existing entities, e.g. child lists. */
    related?: React.ReactNode;
}

export const EntityDetailLayout: React.FC<Props> = ({
    label, section, title, breadcrumbs, editor, missing, parentPath, deleteWarning, related, children,
}) => {
    const classes = useStyles();
    const history = useHistory();
    const allowNavigation = useRef(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [confirmRestore, setConfirmRestore] = useState(false);
    const id = editor.entity.id;

    async function save() {
        allowNavigation.current = true;
        await editor.save();
        allowNavigation.current = false;
    }

    async function remove() {
        allowNavigation.current = true;
        const ok = await editor.remove(parentPath);
        allowNavigation.current = false;
        if (!ok) setConfirmDelete(false);
    }

    async function restore() {
        allowNavigation.current = true;
        const ok = await editor.save();
        allowNavigation.current = false;
        if (!ok) setConfirmRestore(false);
    }

    const subtitle = editor.isNew
        ? `Create a new ${label.toLowerCase()}`
        : editor.meta
            ? `Last changed ${formatDateTime(editor.meta.versionDateTime)}${editor.meta.creationUser ? " by " + editor.meta.creationUser : ""}`
            : undefined;

    const actions = editor.isNew || editor.loadError ? undefined : editor.isVersion ? (
        <>
            <Button variant="outlined" startIcon={<MdHistory />} onClick={() => history.push(sysadminPaths.versions(section, id))}>
                All versions
            </Button>
            <Button variant="contained" color="primary" startIcon={<MdRestore />} onClick={() => setConfirmRestore(true)} disabled={editor.loading}>
                Restore this version
            </Button>
        </>
    ) : (
        <>
            <Button variant="outlined" startIcon={<MdHistory />} onClick={() => history.push(sysadminPaths.versions(section, id))}>
                History
            </Button>
            <Button className={classes.deleteButton} startIcon={<MdDeleteOutline />} onClick={() => setConfirmDelete(true)} disabled={editor.loading}>
                Delete
            </Button>
        </>
    );

    return (
        <div>
            <Prompt
                when={editor.dirty && !editor.isVersion}
                message={() => allowNavigation.current || "You have unsaved changes. Leave the page anyway?"}
            />
            <PageHeader title={title} subtitle={subtitle} breadcrumbs={breadcrumbs} actions={actions} />

            {editor.isVersion && editor.meta && (
                <Alert severity="info" className={classes.banner}>
                    You are viewing a previous version from {formatDateTime(editor.meta.versionDateTime)}
                    {editor.meta.creationUser ? ` (changed by ${editor.meta.creationUser})` : ""}. Fields are read-only.
                    Restoring makes this the current version.
                </Alert>
            )}

            {editor.loadError ? (
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={editor.reloadEntity}>Retry</Button>}>
                    {editor.loadError}
                </Alert>
            ) : editor.loading ? (
                <Paper variant="outlined" className={classes.section}>
                    <Skeleton height={40} />
                    <Skeleton height={40} />
                    <Skeleton height={40} width="60%" />
                </Paper>
            ) : (
                <>
                    {children}
                    {!editor.isVersion && (editor.isNew || editor.dirty) && (
                        <SaveBar
                            missing={missing}
                            info={editor.isNew ? "" : "Unsaved changes"}
                            saving={editor.saving}
                            canSave={editor.isNew || editor.dirty}
                            saveLabel={editor.isNew ? `Create ${label.toLowerCase()}` : "Save changes"}
                            onCancel={() => (editor.isNew ? history.push(parentPath) : editor.reset())}
                            onSave={save}
                        />
                    )}
                    {!editor.isNew && !editor.isVersion && related}
                </>
            )}

            <ConfirmDialog
                open={confirmDelete}
                title={`Delete ${label.toLowerCase()}?`}
                message={
                    <>
                        <p><b>{title}</b> will be deleted.</p>
                        {deleteWarning}
                    </>
                }
                confirmLabel="Delete"
                destructive
                onConfirm={remove}
                onClose={() => setConfirmDelete(false)}
            />
            <ConfirmDialog
                open={confirmRestore}
                title="Restore this version?"
                message={`The current ${label.toLowerCase()} will be replaced by the version from ${formatDateTime(editor.meta?.versionDateTime)}.`}
                confirmLabel="Restore"
                onConfirm={restore}
                onClose={() => setConfirmRestore(false)}
            />
        </div>
    );
};
